// PlateForm3.tsx
// see PlateForm.tsx, PlateForm2.tsx
// добавлена функциональность для вызова из дашборда
// изменен дмзайн
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { EntityPath, Plate } from '../../../types/plate';
import { Group } from '../../../types/group';
import { getErrorMessage } from '../../../tools/error';
import CustomButtonRadio from '../../CustomButtonRadio';
import { MultiSelectField } from './MultiSelectField';
import { ReactionType } from '../../../types/reaction';
import dayjs from 'dayjs';
import { LocalizedDateTimePicker } from '../../localizedDatePicker/LocalizedDateTimePicker';
import { useAuthStore } from '../../../store/useAuthStore3';
import { Subtenant } from '../../../api/subtenantApi';

export interface PlateFormProps {
  title?: string,
  subtitle?: string,
  plate: Plate | null;
  onSave: (values: Plate) => void;
  onCancel: () => void;
  groups: Group[];
  subtenants: Subtenant[];
  entityPaths: EntityPath[];
  lockPointId?: boolean;
  reactionTypes: ReactionType[];
}

export const PassType = {
  Permanent: 1,
  OneTime: 2,
  Temporary: 3,
} as const;

export type PassType = typeof PassType[keyof typeof PassType];

function stringToLocalInputValue(isoString: string): string {
  if (!isoString) return "";
  const d = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toUTCISOString(localString: string): string {
  const d = new Date(localString);
  return d.toISOString();
}

// advice from Gemini
const PERMANENT_DATE_STRING = '2099-12-31T23:59:59.999';

const getOtpExpiryDate = (): string => {
  const date = new Date();
  // Если сейчас 17:00 или позже, ставим дату на завтра
  if (date.getHours() >= 17) {
    date.setDate(date.getDate() + 1);
  }
  // Устанавливаем конец дня (23:59:59) по местному времени
  date.setHours(23, 59, 59, 999);
  
  // Формируем строку в формате 'YYYY-MM-DDTHH:mm', 
  // которую функция toUTCISOString превратит в ISO
  const pad = (n: number) => String(n).padStart(2, "0");
  const localIso = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  
  return localIso;
};

const PlateForm: React.FC<PlateFormProps> = ({
  title,
  subtitle,
  plate,
  onSave,
  onCancel,
  groups,
  subtenants,
  entityPaths,
  lockPointId = false,
  reactionTypes,
}) => {
  const { t } = useTranslation();
  const { customerId } = useAuthStore();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // const [selectedOption, setSelectedOption] = React.useState<PassType>(PassType.Permanent);
  const [selectedOption, setSelectedOption] = React.useState<PassType>(() => {
    if (!plate) {
      return PassType.Permanent;
    }
    if (plate.is_otp) {
      return PassType.OneTime;
    }
    if (plate.valid_to == PERMANENT_DATE_STRING) { // костыль
      return PassType.Temporary;
    }
    return PassType.Permanent;
  });

  const REACTION_OPTIONS = reactionTypes ? 
    // reactionTypes.map(r => ({value: r.reaction_type_id, label: r.name})) : 
    reactionTypes.map(r => ({value: r.id, label: r.name})) : 
    [];
  
  const initialValues: Plate = plate || {
    customer_id: customerId || -1,
    id: null,
    group_id: groups.length == 1 ? groups[0].group_id :  ('' as unknown as number),
    registration_number: '',
    car_brand: '',
    car_owner: '',
    valid_since: toLocalInputValue(new Date()),
    valid_to: PERMANENT_DATE_STRING,
    is_otp: false,
    is_enabled: true,
    reactions: [],
  };

  const validationSchema = Yup.object({
    registration_number: Yup.string().required(('registration_number_is_required')),
    group_id: Yup.number().required(('group_is_required')),
    valid_since: Yup.date().required(('valid_since_is_required')),
    valid_to: Yup.date().required(('valid_to_is_required')),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  const subtenantMap = new Map<number, string>(
    subtenants.map(s => [s.subtenant_id, s.name])
  );

  const groupsExt = groups.map(g => {
    if (g.subtenant_id && subtenantMap.has(g.subtenant_id)) {
        return {...g, name: `${subtenantMap.get(g.subtenant_id)} / ${g.name}` };
    }
    return g;
  })
  .sort((a, b) => {
    const hasA = +!!(a.subtenant_id && subtenantMap.has(a.subtenant_id));
    const hasB = +!!(b.subtenant_id && subtenantMap.has(b.subtenant_id));
    // Разность даст: 0 (если оба в одной категории), -1 (если A без ID, а B с ID), 1 (наоборот)
    // Если категории разные (разность не 0), возвращаем её. Если одинаковые (0) — сортируем по алфавиту.
    return (hasA - hasB) || a.name.localeCompare(b.name);
  });  

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      validateOnMount={true}
      onSubmit={ async (values, { setSubmitting, setStatus }) => {
        setSubmitError(null);
        try {
          await onSave(values);
        } catch (error) {
          console.error("Save failed:", error);
          const errorMessage = getErrorMessage(
            error, 
            t('PlateForm.Failed_to_save_plate_general') // Сообщение по умолчанию
          );
          setSubmitError(errorMessage);
          setSubmitting(false);        
        }
      }}
    >
      {(formikProps: FormikProps<Plate>) => {
        const { values, setFieldValue } = formikProps;

        const handleChange = (val: PassType)=> { 
          console.log("New value selected:", val);
          setSelectedOption(val);
          switch(val) {
            case PassType.Permanent:
              setFieldValue('is_otp', false);
              setFieldValue('valid_to', toUTCISOString(PERMANENT_DATE_STRING));
              break;
            case PassType.OneTime:
              setFieldValue('is_otp', true);
              const otpDate = getOtpExpiryDate();
              setFieldValue('valid_to', toUTCISOString(otpDate));
              break;
            case PassType.Temporary:
              setFieldValue('is_otp', false);
              break;
          }
        }

        return (
          <Form>
            <Typography variant='h4'>
              {title}
            </Typography>
            <Stack spacing={2} sx={{ mt: 2 }} direction={'column'}>
            <Typography variant='h6'>
              {subtitle}
            </Typography>
            <CustomButtonRadio
              // label={title}
              options={[
                {value: PassType.Permanent, label: t('PlateForm.Permanent')/*'Постійна'*/},
                {value: PassType.OneTime, label: t('PlateForm.OneTimePass')/*'Разова'*/},
                {value: PassType.Temporary, label: t('PlateForm.Temporary')/*'Тимчасова'*/},
              ]}
              value={selectedOption}
              onChange={(val: number) => handleChange(val as PassType)}
              orientation='horizontal'
            />
            </Stack>

            <Stack spacing={2} sx={{ mt: 2 }}>

              {submitError && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {submitError}
                </Typography>
              )}

              <Field name="registration_number">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('PlateForm.registration_number_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Field name="car_owner">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('PlateForm.car_owner_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Field name="tag">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('PlateForm.tag_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Field name="car_brand">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('PlateForm.car_brand_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Field name="group_id">
                {({ field, meta }: { field: any; meta: any }) => (
                  <FormControl fullWidth error={meta.touched && !!meta.error}>
                    <InputLabel id="point-id-label">{t('PlateForm.group_Label')}</InputLabel>
                    <Select
                      labelId="group-id-label"
                      label={t('PlateForm.group_Label')}
                      {...field}
                      value={field.value ?? ''}
                      disabled={lockPointId}
                    >
                      <MenuItem value=""><em>{t('PlateForm.Select_a_Group')}</em></MenuItem>
                      {groupsExt.map((group) => (
                        <MenuItem key={group.group_id} value={group.group_id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && (
                      <Typography color="error" variant="caption" component="div">{meta.error}</Typography>
                    )}
                  </FormControl>
                )}
              </Field>


              <Field name="is_enabled">
                {({ field }: FieldProps) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value}
                        onChange={field.onChange}
                        name={field.name}
                      />
                    }
                    label={t('PlateForm.is_enabled_Label')}
                  />
                )}
              </Field>

              <Field
                name="reactions"
                label={t('PlateForm.reactions_Label')}
                options={REACTION_OPTIONS}
              >
                {(fieldProps: FieldProps<number[]>) => (
                  <MultiSelectField 
                    {...fieldProps} 
                    label={t('PlateForm.reactions_Label')} 
                    options={REACTION_OPTIONS} 
                  />
                )}
              </Field>              

              <Stack direction="row" spacing={2}>

                {/* <Field name="valid_since">
                  {({ field, meta }: { field: any; meta: any }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('PlateForm.valid_since_Label')}
                      type='datetime-local'
                      value={stringToLocalInputValue(values.valid_since)}
                      onChange={(e) =>
                        setFieldValue("valid_since", toUTCISOString(e.target.value))
                      }
                      slotProps={{ shrink: true }}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field> */}
                  <Field name="valid_since">
                    {({ field, meta }: any) => (
                      <LocalizedDateTimePicker
                        {...field}
                        label={t('PlateForm.valid_since_Label')}
                        // Формат, который видит пользователь
                        // format="DD.MM.YYYY HH:mm" 
                        // Превращаем строку из Formik в объект dayjs для MUI
                        value={field.value ? dayjs(field.value) : null}
                        // При изменении превращаем объект dayjs обратно в ISO строку для Formik
                        onChange={(newValue) => {
                          setFieldValue("valid_since", newValue ? newValue.toISOString() : '');
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: meta.touched && !!meta.error,
                            helperText: meta.touched && meta.error,
                          },
                        }}
                      />
                    )}
                  </Field>

                  <Field name="valid_to">
                    {({ field, meta }: any) => (
                      <LocalizedDateTimePicker
                        {...field}
                        label={t('PlateForm.valid_to_Label')}
                        // format="DD.MM.YYYY HH:mm" 
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(newValue) => {
                          setFieldValue("valid_to", newValue ? newValue.toISOString() : '');
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: meta.touched && !!meta.error,
                            helperText: meta.touched && meta.error,
                          },
                        }}
                      />
                    )}
                  </Field>

                {/* <Field name="valid_to">
                  {({ field, meta }: { field: any; meta: any }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={t('PlateForm.valid_to_Label')}
                      type='datetime-local'
                      value={stringToLocalInputValue(values.valid_to)}
                      onChange={(e) =>
                        setFieldValue("valid_to", toUTCISOString(e.target.value))
                      }
                      slotProps={{ shrink: true }}
                      error={meta.touched && !!meta.error}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field> */}

              </Stack>

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined' onClick={ async ()=> {
                  onCancel();
                }}>
                  {t('OriginForm.Cancel')}
                </Button>

                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid}>
                  {formikProps.isSubmitting ? t('OriginForm.Saving') : t('OriginForm.Save')}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default PlateForm;